import { useEffect, useRef, useState } from 'preact/hooks';
import { useIntl } from 'shared/intl/use-intl';
import { showToast } from '@components/toaster';
import { captureException } from 'client/lib/sentry';
import {
  canRecord,
  captureErrorKind,
  CaptureErrorKind,
  formatDuration,
  Recording,
  recordingName,
  startRecording,
  stopStream,
  VIDEO_TYPES,
  VIDEO_BITS_PER_SECOND,
  AUDIO_BITS_PER_SECOND,
  maxRecordingSeconds,
} from 'client/lib/media-recording';
import { globalConfig } from 'client/lib/auth';
import { knownMaxUploadSize } from 'client/lib/upload-limits';

export type WebcamMode = 'video' | 'photo';

type Props = {
  mode: WebcamMode;
  onDone(file: File): void;
  onCancel(): void;
  onHasDataChange?(hasData: boolean): void;
};

type Step = 'starting' | 'ready' | 'recording' | 'review';

const lightButton =
  'px-4 py-1.5 rounded-full border border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-800 text-sm text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700';
const primaryButton =
  'px-4 py-1.5 rounded-full bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60';
const recordButton =
  'px-4 py-1.5 rounded-full bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-60';

function useErrorMessage(mode: WebcamMode) {
  const intl = useIntl();
  return (kind: CaptureErrorKind) => {
    if (kind === 'blocked') {
      return mode === 'video'
        ? intl(
            'Your camera or microphone is blocked for this site. Allow them from the icon in the address bar, then try again.',
          )
        : intl(
            'Your camera is blocked for this site. Allow it from the icon in the address bar, then try again.',
          );
    }
    if (kind === 'missing') {
      return intl('No camera found. Connect one, then try again.');
    }
    if (kind === 'busy') {
      return intl('Another app is using your camera. Close it, then try again.');
    }
    if (kind === 'unsupported') {
      return intl(
        "This browser can't use your camera. Record on your device, then add the file with Add photo, video or file.",
      );
    }
    return intl("The camera didn't start. Try again.");
  };
}

function nextFrame() {
  return new Promise((resolve) => requestAnimationFrame(resolve));
}

async function showPreview(getVideo: () => HTMLVideoElement | null, stream: MediaStream) {
  let video = getVideo();
  for (let tries = 0; !video && tries < 10; tries++) {
    await nextFrame();
    video = getVideo();
  }
  if (!video) {
    return;
  }
  video.srcObject = stream;
  void video.play().catch(() => {});
  if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA && video.videoWidth) {
    return Promise.resolve();
  }
  return new Promise<void>((resolve) => {
    const timeout = setTimeout(resolve, 5000);
    video.addEventListener(
      'loadeddata',
      () => {
        clearTimeout(timeout);
        resolve();
      },
      { once: true },
    );
  });
}

function snapshot(video: HTMLVideoElement) {
  return new Promise<Blob | undefined>((resolve) => {
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx || !canvas.width) {
      resolve(undefined);
      return;
    }
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => resolve(blob || undefined), 'image/jpeg', 0.92);
  });
}

export function WebcamCapture({ mode, onDone, onCancel, onHasDataChange }: Props) {
  const intl = useIntl();
  const errorMessage = useErrorMessage(mode);
  const [attempt, setAttempt] = useState(0);
  const [step, setStep] = useState<Step>('starting');
  const [error, setError] = useState<CaptureErrorKind | undefined>(undefined);
  const [seconds, setSeconds] = useState(0);
  const [result, setResult] = useState<{ blob: Blob; url: string } | undefined>(undefined);
  const stream = useRef<MediaStream | undefined>(undefined);
  const recording = useRef<Recording | undefined>(undefined);
  const timer = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const preview = useRef<HTMLVideoElement>(null);
  const isMounted = useRef(true);
  const root = useRef<HTMLDivElement>(null);
  const stopRef = useRef(() => {});
  const wasInterrupted = useRef(false);
  const hitLimit = useRef(false);
  const [limit] = useState(() => maxRecordingSeconds(knownMaxUploadSize(globalConfig()?.user)));
  const title = mode === 'video' ? intl('Record video') : intl('Take photo');

  useEffect(
    () => () => {
      isMounted.current = false;
      clearInterval(timer.current);
      recording.current?.cancel();
      stopStream(stream.current);
    },
    [],
  );

  useEffect(() => {
    if (!result) {
      return;
    }
    return () => URL.revokeObjectURL(result.url);
  }, [result]);

  const fail = (kind: CaptureErrorKind) => {
    clearInterval(timer.current);
    recording.current?.cancel();
    recording.current = undefined;
    stopStream(stream.current);
    stream.current = undefined;
    setError(kind);
  };

  useEffect(() => {
    let disposed = false;
    setError(undefined);
    setStep('starting');

    async function begin() {
      const isSupported = mode === 'photo' ? !!navigator.mediaDevices?.getUserMedia : canRecord();
      if (!isSupported) {
        setError('unsupported');
        return;
      }
      try {
        const media = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' },
          audio: mode === 'video',
        });
        if (disposed) {
          stopStream(media);
          return;
        }
        stream.current = media;
        await showPreview(() => preview.current, media);
        if (disposed) {
          return;
        }
        setStep('ready');
      } catch (err) {
        const kind = captureErrorKind(err);
        if (kind === 'failed') {
          captureException(err as Error);
        }
        if (!disposed) {
          fail(kind);
        }
      }
    }
    void begin();

    return () => {
      disposed = true;
      stopStream(stream.current);
      stream.current = undefined;
    };
  }, [attempt]);

  useEffect(() => {
    onHasDataChange?.(step === 'recording' || step === 'review');
  }, [step]);

  useEffect(() => {
    root.current?.querySelector<HTMLElement>('[data-autofocus]:not(:disabled)')?.focus();
  }, [step, error]);

  const start = async () => {
    const video = preview.current;
    if (!stream.current || !video) {
      return;
    }
    if (mode === 'photo') {
      const blob = await snapshot(video);
      if (!isMounted.current) {
        return;
      }
      if (!blob) {
        fail('failed');
        return;
      }
      setResult({ blob, url: URL.createObjectURL(blob) });
      setStep('review');
      return;
    }
    try {
      wasInterrupted.current = false;
      hitLimit.current = false;
      recording.current = startRecording(
        stream.current,
        VIDEO_TYPES,
        () => {
          wasInterrupted.current = true;
          stopRef.current();
        },
        { videoBitsPerSecond: VIDEO_BITS_PER_SECOND, audioBitsPerSecond: AUDIO_BITS_PER_SECOND },
      );
    } catch (err) {
      captureException(err as Error);
      fail('failed');
      return;
    }
    const startedAt = Date.now();
    setSeconds(0);
    timer.current = setInterval(() => {
      const elapsed = (Date.now() - startedAt) / 1000;
      setSeconds(elapsed);
      if (limit !== undefined && elapsed >= limit) {
        hitLimit.current = true;
        stopRef.current();
      }
    }, 250);
    setStep('recording');
  };

  const stop = async () => {
    const current = recording.current;
    if (!current) {
      return;
    }
    recording.current = undefined;
    clearInterval(timer.current);
    try {
      const blob = await current.stop();
      if (!isMounted.current) {
        return;
      }
      if (!blob.size) {
        setStep('ready');
        return;
      }
      setResult({ blob, url: URL.createObjectURL(blob) });
      setStep('review');
      if (hitLimit.current) {
        showToast({
          type: 'warn',
          title: intl('Recording stopped'),
          message: intl('This video reached the longest length you can upload.'),
        });
      } else if (wasInterrupted.current) {
        showToast({
          type: 'warn',
          title: intl('Recording stopped'),
          message: intl(
            'The camera or microphone disconnected. The video keeps what was recorded.',
          ),
        });
      }
    } catch (err) {
      captureException(err as Error);
      fail('failed');
    }
  };

  stopRef.current = stop;

  const retake = () => {
    setResult(undefined);
    if (stream.current?.getTracks().some((track) => track.readyState === 'ended')) {
      setAttempt((n) => n + 1);
      return;
    }
    setStep('ready');
  };

  const use = () => {
    if (!result) {
      return;
    }
    const type = result.blob.type || (mode === 'video' ? 'video/webm' : 'image/jpeg');
    const prefix = mode === 'video' ? intl('Video') : intl('Photo');
    onDone(new File([result.blob], recordingName(prefix, type), { type }));
  };

  if (error) {
    return (
      <div ref={root} class="flex flex-col gap-3 p-3" role="alert">
        <p class="text-sm text-red-700 dark:text-red-300">{errorMessage(error)}</p>
        <div class="flex justify-end gap-2">
          <button type="button" class={lightButton} onClick={onCancel}>
            {intl('Cancel')}
          </button>
          {error !== 'unsupported' && (
            <button
              type="button"
              class={primaryButton}
              data-autofocus
              onClick={() => setAttempt((n) => n + 1)}
            >
              {intl('Try again')}
            </button>
          )}
        </div>
      </div>
    );
  }

  const isReview = step === 'review' && result;

  return (
    <div ref={root} class="flex flex-col">
      <div class="relative aspect-video bg-gray-900 overflow-hidden">
        {isReview && mode === 'video' && (
          <video src={result.url} controls playsInline class="w-full h-full object-contain" />
        )}
        {isReview && mode === 'photo' && (
          <img src={result.url} alt={intl('Photo preview')} class="w-full h-full object-contain" />
        )}
        <video
          ref={preview}
          muted
          playsInline
          autoPlay
          class={`w-full h-full object-cover -scale-x-100 ${isReview ? 'hidden' : ''}`}
        />
        {step === 'starting' && (
          <span class="absolute inset-0 flex items-center justify-center text-sm text-gray-300">
            {intl('Starting camera...')}
          </span>
        )}
        {step === 'recording' && (
          <span class="absolute top-2 left-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-black/60 text-white text-xs tabular-nums">
            <span class="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            {formatDuration(seconds)}
            {limit !== undefined && ` / ${formatDuration(limit)}`}
          </span>
        )}
      </div>
      <div class="flex items-center gap-2 p-3">
        <span class="grow text-sm font-semibold text-gray-800 dark:text-gray-100 tabular-nums">
          {step === 'recording' ? intl('Recording') : title}
        </span>
        {step === 'ready' || step === 'starting' ? (
          <>
            <button type="button" class={lightButton} onClick={onCancel}>
              {intl('Cancel')}
            </button>
            <button
              type="button"
              class={mode === 'video' ? recordButton : primaryButton}
              disabled={step !== 'ready'}
              data-autofocus
              onClick={start}
            >
              {mode === 'video' ? intl('Start') : intl('Take photo')}
            </button>
          </>
        ) : step === 'recording' ? (
          <button type="button" class={recordButton} data-autofocus onClick={stop}>
            {intl('Stop')}
          </button>
        ) : (
          <>
            <button type="button" class={lightButton} onClick={retake}>
              {intl('Retake')}
            </button>
            <button type="button" class={primaryButton} data-autofocus onClick={use}>
              {mode === 'video' ? intl('Use video') : intl('Use photo')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
